import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import './styles/styles.css';
import MainScreen from './components/MainScreen';
import LoadingScreen from './components/LoadingScreen';
import SuccessScreen from './components/SuccessScreen';
import OutOfPovsScreen from './components/OutOfPovsScreen';
import DownloadPopup from './components/DownloadPopup';
import { GraphQLService } from './services/graphqlService';
import { mockContact, mockVibes, getRandomItems } from './data/mockData';
import { validateName } from './utils/validation';

// POV Component for dynamic slug-based routing
function PovApp() {
    const { slugParam } = useParams();
    
    // Extract slug from URL parameter (e.g., "slug=p7c2gCXV7y")
    const slug = GraphQLService.extractSlugFromParam(slugParam);
    
    return <MobileVibesApp slug={slug} />;
}

// Main Mobile Vibes App Component
function MobileVibesApp({ slug = null }) {
    const [appState, setAppState] = useState({
        isLoading: slug ? true : false, // Show loading if we have a slug to fetch
        isSending: false,
        contact: slug ? null : mockContact, // Use null for dynamic, mockContact for static
        selectedVibe: null,
        randomVibes: [],
        customMessage: '',
        currentScreen: 'main',
        refreshCount: 0,
        maxRefreshes: 1,
        showDownloadPopup: false,
        error: null,
        userNotFound: false
    });

    // Initialize app data based on whether we have a slug or not
    useEffect(() => {
        const loadAppData = async () => {
            if (slug) {
                // Dynamic mode: fetch data by slug
                try {
                    setAppState(prev => ({ ...prev, isLoading: true, error: null }));
                    
                    // Load user data and vibes in parallel
                    const [userData, vibesData] = await Promise.all([
                        GraphQLService.getUserByUsername(slug),
                        GraphQLService.getRandomVibesForUsername(slug, 4)
                    ]);

                    setAppState(prev => ({
                        ...prev,
                        isLoading: false,
                        contact: userData,
                        randomVibes: vibesData.length > 0 ? vibesData : getRandomItems(mockVibes, 4),
                        userNotFound: false
                    }));
                    
                    updatePageTitle(userData);
                } catch (error) {
                    console.error('Error loading user data for slug:', slug, error);
                    setAppState(prev => ({
                        ...prev,
                        isLoading: false,
                        error: error.message,
                        userNotFound: true
                    }));
                }
            } else {
                // Static mode: use mock data (original behavior)
                const initialVibes = getRandomItems(mockVibes, 4);
                setAppState(prev => ({
                    ...prev,
                    randomVibes: initialVibes,
                    contact: mockContact
                }));
                updatePageTitle(mockContact);
            }
        };

        loadAppData();
    }, [slug]);

    const updatePageTitle = (contact) => {
        if (contact && contact.username) {
            document.title = `POV ${contact.username}`;
        } else {
            document.title = 'POV';
        }
    };

    const setCurrentScreen = (screen) => {
        setAppState(prev => ({ ...prev, currentScreen: screen }));
    };

    const setSelectedVibe = (vibe) => {
        setAppState(prev => ({ ...prev, selectedVibe: vibe }));
    };

    const setCustomMessage = (message) => {
        setAppState(prev => ({ ...prev, customMessage: message }));
    };

    const refreshOptions = async () => {
        // Increment refresh count first
        const newRefreshCount = appState.refreshCount + 1;
        
        // Check if user has exceeded refresh limit (after 2 successful refreshes)
        if (newRefreshCount > appState.maxRefreshes) {
            showDownloadPopup();
            return;
        }

        try {
            let newVibes;
            
            if (slug) {
                // Dynamic mode: try to get new vibes from API
                try {
                    newVibes = await GraphQLService.getRandomVibesForUsername(slug, 4);
                    if (newVibes.length === 0) {
                        newVibes = getRandomItems(mockVibes, 4);
                    }
                } catch (error) {
                    console.error('Error refreshing vibes for slug:', error);
                    newVibes = getRandomItems(mockVibes, 4);
                }
            } else {
                // Static mode: use mock data
                newVibes = getRandomItems(mockVibes, 4);
            }
            
            setAppState(prev => ({
                ...prev,
                randomVibes: newVibes,
                refreshCount: newRefreshCount,
                selectedVibe: null,
                customMessage: ''
            }));
        } catch (error) {
            console.error('Error in refreshOptions:', error);
            // Fallback to mock data
            const fallbackVibes = getRandomItems(mockVibes, 4);
            setAppState(prev => ({
                ...prev,
                randomVibes: fallbackVibes,
                refreshCount: newRefreshCount,
                selectedVibe: null,
                customMessage: ''
            }));
        }
    };

    const handleSend = async () => {
        if (!appState.selectedVibe) return;
        
        // Validate name before proceeding
        if (!validateName(appState.customMessage)) {
            console.error('Cannot send vibe: invalid name');
            return;
        }

        setAppState(prev => ({ ...prev, isSending: true }));
        setCurrentScreen('loading');

        try {
            if (slug) {
                // Dynamic mode: send via GraphQL API
                await GraphQLService.sendAnonymousVibe(
                    slug,
                    appState.selectedVibe.id,
                    appState.customMessage
                );
            } else {
                // Static mode: mock API call
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
            setCurrentScreen('success');
        } catch (error) {
            console.error('Error sending vibe:', error);
            // Still show success for better UX, but log the error
            setCurrentScreen('success');
        } finally {
            setAppState(prev => ({ ...prev, isSending: false }));
        }
    };

    const returnToMainScreen = () => {
        setAppState(prev => ({
            ...prev,
            selectedVibe: null,
            customMessage: '',
            currentScreen: 'main',
            refreshCount: 0  // Reset refresh count when returning to main screen
        }));
    };

    const showDownloadPopup = () => {
        setAppState(prev => ({ ...prev, showDownloadPopup: true }));
    };

    const closeDownloadPopup = () => {
        setAppState(prev => ({ ...prev, showDownloadPopup: false }));
    };

    const downloadApp = async () => {
        console.log('downloadApp function called');
        try {
            // Enhanced platform detection using user agent
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            console.log('User agent:', userAgent);

            const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
            const isAndroid = /android/i.test(userAgent);

            // App configuration
            const packageName = 'com.terer.zap';
            const appScheme = 'povapp://';
            const iosAppId = '6742169228';
            const playStoreUrl = `https://play.google.com/store/apps/details?id=${packageName}`;
            const appStoreWebUrl = `https://apps.apple.com/my/app/pov-social/id${iosAppId}`;

            if (isIOS) {
                // iOS - try to open app first, fallback to App Store
                console.log('iOS detected - attempting to open app');

                // Close popup
                closeDownloadPopup();

                // Track if app opened
                let appOpened = false;

                // Listen for page visibility change (indicates app opened)
                const handleVisibilityChange = () => {
                    if (document.hidden) {
                        appOpened = true;
                        console.log('App opened successfully');
                    }
                };
                document.addEventListener('visibilitychange', handleVisibilityChange);

                // Try to open the app using deep link
                try {
                    window.location = appScheme;
                } catch (e) {
                    console.log('Deep link attempt:', e);
                }

                // Fallback: If app doesn't open within 1.5s, redirect to App Store
                setTimeout(() => {
                    document.removeEventListener('visibilitychange', handleVisibilityChange);

                    if (!appOpened && !document.hidden) {
                        console.log('App not installed, redirecting to App Store');
                        window.location.assign(appStoreWebUrl);
                    }
                }, 1500);
            } else if (isAndroid) {
                // Android - use Intent URL with fallback
                console.log('Android detected - attempting to open app');

                // Try Intent URL first
                const intentUrl = `intent://open/#Intent;scheme=${appScheme.replace('://', '')};package=${packageName};end`;
                window.location.href = intentUrl;

                // Fallback to Play Store if needed
                setTimeout(() => {
                    // If page is still visible, open Play Store
                    if (!document.hidden) {
                        console.log('Opening Play Store as fallback');
                        window.location.href = playStoreUrl;
                    }
                }, 2500);

                // Close popup
                setTimeout(() => {
                    closeDownloadPopup();
                }, 100);
            } else {
                // Desktop or other - redirect to main website
                console.log('Desktop detected - opening POV website');
                window.location.href = 'https://getpov.app/';

                // Close popup
                setTimeout(() => {
                    closeDownloadPopup();
                }, 100);
            }
        } catch (error) {
            console.error('Error in downloadApp:', error);
            // Fallback - still try to close popup
            closeDownloadPopup();
        }
    };

    const shareApp = () => {
        const text = "I'm literally carrying you to greatness - download POV app now. 🚀\nhttps://getpov.app/invite.html";
        
        if (navigator.share) {
            navigator.share({
                title: 'POV Social',
                text: text,
                url: 'https://getpov.app/invite.html'
            });
        } else {
            // Fallback for browsers that don't support native sharing
            navigator.clipboard.writeText(text).then(() => {
                alert('Message copied to clipboard!');
            }).catch(() => {
                // If clipboard fails, show the text for manual copy
                alert('Share this message:\n\n' + text);
            });
        }
    };

    const renderCurrentScreen = () => {
        // Show loading screen while fetching user data
        if (appState.isLoading) {
            return <LoadingScreen />;
        }

        // Show error screen if user not found (only in dynamic mode)
        if (slug && (appState.userNotFound || appState.error)) {
            return (
                <div className="error-container">
                    <div className="error-content">
                        <h2>User Not Found</h2>
                        <p>{appState.error || 'The slug you entered does not exist.'}</p>
                        <p>Please check the URL and try again.</p>
                    </div>
                </div>
            );
        }

        switch (appState.currentScreen) {
            case 'loading':
                return <LoadingScreen />;
            case 'success':
                return (
                    <SuccessScreen
                        contact={appState.contact}
                        downloadApp={downloadApp}
                        onReturnToMain={returnToMainScreen}
                    />
                );
            case 'out-of-povs':
                return (
                    <OutOfPovsScreen
                        onNavigateBack={returnToMainScreen}
                        onInviteFriends={shareApp}
                    />
                );
            default:
                return (
                    <MainScreen
                        contact={appState.contact}
                        randomVibes={appState.randomVibes}
                        selectedVibe={appState.selectedVibe}
                        customMessage={appState.customMessage}
                        isSending={appState.isSending}
                        refreshCount={appState.refreshCount}
                        maxRefreshes={appState.maxRefreshes}
                        onVibeSelect={setSelectedVibe}
                        onMessageChange={setCustomMessage}
                        onRefreshOptions={refreshOptions}
                        onSend={handleSend}
                    />
                );
        }
    };

    return (
        <div className="mobile-container">
            {renderCurrentScreen()}
            {appState.showDownloadPopup && (
                <DownloadPopup
                    onClose={closeDownloadPopup}
                    downloadApp={downloadApp}
                />
            )}
        </div>
    );
}

// Main App component with routing
function App() {
    return (
        <Router basename="/pov">
            <Routes>
                {/* Dynamic route for slugs: /pov/slug=p7c2gCXV7y */}
                <Route path="/:slugParam" element={<PovApp />} />
                {/* Default route shows original static app */}
                <Route path="/" element={<MobileVibesApp />} />
            </Routes>
        </Router>
    );
}

export default App;